
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useMemo, useState } from 'react';
import View from '../../../components/layout/View';
import { buildSessionData, withPageSession } from '../../../lib/session';
import { CmsPageIdentifier } from '../../../lib/content';
import { NextPageWithLayout } from '../../_app';
import { PageFilterType } from '../../../filter/types';
import { useAccountClient, useContentClient } from '../../../lib/api-client';
import useTranslation from 'next-translate/useTranslation';
import { playCasinoGame, playLiveCasinoGame, useCasinoGameViewModel, useRecentlyPlayedCasinoGames, } from '../../../lib/gaming';
import { isLoggedInCustomer, useUser, useUserAccounts } from '../../../lib/user';
import { useRouter } from 'next/router';
import HelpText from '../../../components/HelpText';
import Switch from '../../../components/forms/elements/Switch';
import { CasinoGamesCategory } from '../../../components/casino/CasinoGamesCategory';
import { isDemoModeAvailable } from '../../../util/gaming';
import { gtmPushCasinoGameStartedEvent } from '../../../util/google-tag-manager';
import { CompetitionType } from '@arland-bmnext/api-data';
import { setBodyNotScrollable, setBodyScrollable } from '../../../util/toggleBodyScrollable';
import { useLayoutContext } from '../../../context/layout.context';
import { CasinoPlayDTO } from '@arland-bmnext/webapps-api-data';
import { removeQueryParam } from '../../../util/router';
import { CasinoGameFrame } from '../../../components/casino/CasinoGameFrame';
import { AnimatePresence } from 'framer-motion';
import { CasinoSearch } from '../../../components/casino/CasinoSearch';
import NoSearchResult from '../../../public/no-result.svg';
import Trans from 'next-translate/Trans';
import { BackButton } from '../../../components/core/Button';
const CasinoCategory: NextPageWithLayout = (props: any) => {
    const router = useRouter();
    const { t } = useTranslation('casino');
    const { user } = useUser();
    const { casinoSettings } = props;
    const { mutateUserAccounts } = useUserAccounts();
    const { currentBreakPoint } = useLayoutContext();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [useDemoMode, setUseDemoMode] = useState(null);
    const [availableCasinoGames, casinoCategoriesViewModel] = useCasinoGameViewModel();
    const [_, addToRecentlyPlayedGames] = useRecentlyPlayedCasinoGames();
    const [gamingSession, setGamingSession] = useState<CasinoPlayDTO>(null);
    const categoryIdQuery = router.query?.categoryId as string;
    const dashIndex = categoryIdQuery.indexOf('-');
    const categoryId = dashIndex > 0 ? parseInt(categoryIdQuery.substring(0, dashIndex)) : parseInt(categoryIdQuery);
    const category = casinoCategoriesViewModel?.find((cat) => cat.id === categoryId);
    const searchResult = useMemo(() => {
        return category?.games?.filter((game) => new RegExp('^' + `*${searchTerm.toLowerCase()}*`.replace(/\*/g, '.*') + '$').test(game.name.toLowerCase()) &&
            casinoCategoriesViewModel?.find((cc) => cc.games?.find((c) => c.id === game.id)));
    }, [category, searchTerm]);
    const onSearchTermChange = (ev) => {
        const value = ev.target.value;
        setSearchTerm(value);
    };
    const onCasinoGameClick = async (gameId: number, gameName: string) => {
        const game = availableCasinoGames?.find((game) => game.id === gameId);
        if (useDemoMode && !isDemoModeAvailable(game))
            return;
        onPlayCasinoGame(gameId);
    };
    const onStopCasinoGame = async () => {
        await removeQueryParam(router, 'play');
        setGamingSession(null);
        setBodyScrollable();
        await mutateUserAccounts();
    };
    const onPlayCasinoGame = async (gameId: number): Promise<void> => {
        let gamingSession = null;
        const game = availableCasinoGames?.find((game) => game.id === gameId);
        if (game) {
            if (useDemoMode && !isDemoModeAvailable(game))
                return;
            if (isLoggedInCustomer(user)) {
                gtmPushCasinoGameStartedEvent(user.id, gameId, game.name);
            }
            if ((game.type & CompetitionType.LiveGame) === CompetitionType.LiveGame) {
                const productIdentifier = game.identifier.split('_')[0];
                gamingSession = await playLiveCasinoGame(gameId, useDemoMode ? 'demo' : 'real', currentBreakPoint === 'desktop' ? 'desktop' : 'mobile', productIdentifier);
            }
            else {
                gamingSession = await playCasinoGame(gameId, useDemoMode ? 'demo' : 'real', currentBreakPoint === 'desktop' ? 'desktop' : 'mobile');
            }
            setGamingSession(gamingSession);
            setBodyNotScrollable();
            addToRecentlyPlayedGames(gameId);
        }
    };
    return (<>
      <div className="casino flex flex-col space-y-6 w-full py-4">
        <div className="casino-header flex space-x-2 justify-between items-center px-4 overflow-hidden">
          <div className={'flex items-center overflow-hidden'}>
            <BackButton url={'/casino'}/>
            <h1 className="casino-header-title text-xl font-bold overflow-hidden overflow-ellipsis whitespace-nowrap">
              {category?.name}
            </h1>
          </div>

          {isLoggedInCustomer(user) && casinoSettings?.demoModeEnabled && (<div className="casino-header-demo flex space-x-4 items-center justify-end">
              <div className="casino-header-demo-helptext flex space-x-1 items-center">
                <span className="casino-header-demo-helptext-title text-sm font-semibold">
                  {t('Casino.useDemoMode.title')}
                </span>
                <HelpText title={t('Casino.useDemoMode.title')} description={t('Casino.useDemoMode.description')}/>
              </div>
              <Switch enabled={useDemoMode} onClick={() => setUseDemoMode(!useDemoMode)}/>
            </div>)}
        </div>

        {category && (<>
            <CasinoSearch searchTerm={searchTerm} setSearchTerm={onSearchTermChange} clearSearchTerm={() => setSearchTerm('')}/>

            {searchTerm?.length > 0 && searchResult?.length === 0 && (<div className="casino-search-result-empty flex flex-col space-y-4 items-center p-8">
                <div className="casino-search-result-empty-svg flex text-primary">
                  <NoSearchResult />
                </div>
                <span className="casino-search-result-empty-svg-text text-center">
                  <Trans i18nKey="casino:Casino.noSearchResult" values={{ searchTerm }} components={[<span className="text-primary"/>]}/>
                </span>
              </div>)}

            {searchTerm?.length > 0 && searchResult?.length > 0 && (<CasinoGamesCategory casinoGames={searchResult} layout="grid" onCasinoGameClick={onCasinoGameClick} type="custom" demoModeActive={useDemoMode} casinoSliderAutoPlayDelay={casinoSettings.sliderAutoPlayDelay}/>)}

            {searchTerm?.length === 0 && (<CasinoGamesCategory id={category.id} type={category.type} casinoGames={category.games} onCasinoGameClick={onCasinoGameClick} demoModeActive={useDemoMode} layout={'grid'} casinoSliderAutoPlayDelay={casinoSettings.sliderAutoPlayDelay}/>)}
          </>)}
      </div>

      <AnimatePresence>
        {gamingSession && (<CasinoGameFrame gamingSession={gamingSession} onStopCasinoGame={onStopCasinoGame} isDemoMode={useDemoMode}/>)}
      </AnimatePresence>
    </>);
};
CasinoCategory.getLayout = function getLayout(_t, _pageProps, page) {
    return (<View pageId="casino-category-page" headerProps={{ addStakeSelector: false, addBonusBanner: false }} betSlipToggleDisabled>
      {page}
    </View>);
};
const getServerSideProps = withPageSession(async ({ req, session }, _pageFilterResult, pageData) => {
    const sessionId = session.get('sessionId');
    const token = session.get('token');
    const authenticated = session.get('authenticated');
    const sessionData = buildSessionData(req, sessionId, token);
    const contentClient = useContentClient();
    const accountClient = useAccountClient();
    const user = await accountClient.getUserProfile(sessionData);
    const casinoSettings = await contentClient.getCasinoSettings(sessionData);
    if (!casinoSettings.enabled ||
        (!casinoSettings.visibleToAnonymous && !authenticated) ||
        (authenticated && !user?.betPlacementAndGamingAllowed)) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            ...pageData,
            casinoSettings,
        },
    };
}, CmsPageIdentifier.Casino, [PageFilterType.PageRequiresAuthentication]);
export default CasinoCategory;

    async function __Next_Translate__getServerSideProps__1970be59ee2__(ctx) {
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/casino/category/[categoryId]',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1970be59ee2__ as getServerSideProps }
  